import type {
  ChannelThreadId,
  DraftMessageId,
} from "@redotech/effect-api-shared/ids/definitions";
import type { DraftSendStage } from "@redotech/effect-api-shared/models/support-ticket/support-ticket-notification";
import {
  createCollection,
  localOnlyCollectionOptions,
} from "@tanstack/react-db";

/**
 * Transient, client-only send-lifecycle state for a draft, keyed by draft id.
 * `stage`/`sendAt` aren't server fields; they live here (never synced to a
 * server, like the presence collection) so a drafts refetch can't clobber them.
 * The drafts context overlays them back onto each draft.
 */
export interface DraftSendStateRecord {
  /** Draft id, stringified to match the drafts collection `getKey`. */
  id: string;
  /** Channel thread the send is on; lets messageCreated clear by thread. */
  threadId: string;
  stage: DraftSendStage;
  sendAt: string | undefined;
}

export function createDraftSendStateCollection() {
  // Local-only: an in-memory collection with no server source, so there is
  // nothing to refetch over the transient send state. Writes go through the
  // standard insert/update/delete API in the helpers below.
  return createCollection(
    localOnlyCollectionOptions<DraftSendStateRecord, string>({
      getKey: (item) => item.id,
    }),
  );
}

export type DraftSendStateCollection = ReturnType<
  typeof createDraftSendStateCollection
>;

/** Record (or refine) a draft's transient send-lifecycle state. */
export function setDraftSendState(
  collection: DraftSendStateCollection,
  draftId: DraftMessageId | string,
  threadId: ChannelThreadId | string,
  stage: DraftSendStage,
  sendAt: string | undefined,
): void {
  const id = String(draftId);
  const tid = String(threadId);
  if (collection.has(id)) {
    collection.update(id, (draft) => {
      draft.threadId = tid;
      draft.stage = stage;
      draft.sendAt = sendAt;
    });
  } else {
    collection.insert({ id, threadId: tid, stage, sendAt });
  }
}

/** Drop a draft's send-lifecycle state (undo, failure, or after it sends). */
export function clearDraftSendState(
  collection: DraftSendStateCollection,
  draftId: DraftMessageId | string,
): void {
  const key = String(draftId);
  if (collection.has(key)) {
    collection.delete(key);
  }
}

/**
 * Clear send-lifecycle state for every draft on a thread. Used by messageCreated
 * so a completed send's overlay clears even when the draft row isn't loaded
 * (the user switched tickets).
 */
export function clearDraftSendStateForThread(
  collection: DraftSendStateCollection,
  threadId: ChannelThreadId | string,
): void {
  const tid = String(threadId);
  // Collect first, then delete: don't mutate the collection mid-iteration.
  const keys: string[] = [];
  for (const record of collection.values()) {
    if (record.threadId === tid) {
      keys.push(record.id);
    }
  }
  for (const key of keys) {
    collection.delete(key);
  }
}

/**
 * Overlay the durable send-lifecycle state back onto draft rows (a drafts
 * refetch wipes `sendStage`/`sendAt` off the row). Returns the input array
 * unchanged when nothing is in-flight, preserving reference identity.
 */
export function overlayDraftSendState<
  T extends { id: unknown; sendStage?: DraftSendStage; sendAt?: string },
>(drafts: T[], sendStateById: ReadonlyMap<string, DraftSendStateRecord>): T[] {
  if (sendStateById.size === 0) {
    return drafts;
  }
  return drafts.map((draft) => {
    const sendState = sendStateById.get(String(draft.id));
    return sendState
      ? { ...draft, sendStage: sendState.stage, sendAt: sendState.sendAt }
      : draft;
  });
}
